import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ProjectShowcase.css';

const MOCKUPS = [
  {
    id: 'quran',
    type: 'plain',           // plain = no frame, just image
    label: 'Mobile App',
    tag: 'Islamic · AI Quran',
    color: '#8258c8',
    title: 'Quran AI — Tarteel',
    desc: 'AI-powered Quran memorization with real-time recitation error detection and instant feedback.',
    images: [
      '/assets/images/projects/AI-Quran-App/1.png',
      '/assets/images/projects/AI-Quran-App/2.png',
      '/assets/images/projects/AI-Quran-App/3.png',
    ],
  },
  {
    id: 'medical',
    type: 'browser',
    label: 'Web Platform',
    tag: 'HealthTech · SaaS',
    color: '#45d0bd',
    title: 'Medical Platform',
    desc: 'Zero-error hospital management — appointments, records, and doctor portals.',
    images: [
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png',
      '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.12.10.png',
    ],
  },
  {
    id: 'kiosk',
    type: 'plain',           // plain = no frame, just image
    label: 'Kiosk System',
    tag: 'Restaurant Tech · POS',
    color: '#2c84c8',
    title: 'DineOS Self-Order Kiosk',
    desc: 'Touch-screen self-ordering kiosk — cuts wait times 3× and saves 30% operational cost.',
    images: [
      '/assets/images/kiosk-bg.png',
    ],
  },
  {
    id: 'robot',
    type: 'robot',
    label: 'Robotics',
    tag: 'AI · Therapy · HealthTech',
    color: '#45d0bd',
    title: 'Autism Therapy Robot',
    desc: "AI-powered therapy companion that adapts in real-time to each child's behavior.",
    images: [
      '/assets/images/robot/190610_NB1_Rasiing Hand_with Ear_Ted-1.png',
    ],
  },
  {
    id: 'platform',
    type: 'platform',
    label: 'OnDemand Platform',
    tag: 'Cross-Platform · SaaS',
    color: '#8258c8',
    title: 'OnDemand Platform',
    desc: 'Full cross-platform SaaS — native mobile app for field agents + powerful web dashboard.',
    images: [
      '/assets/images/products/ondemand/Android Compact - 1.png',
      '/assets/images/products/ondemand/Android Compact - 2.png',
      '/assets/images/products/ondemand/MAIN.png',
    ],
  },
];

/* ─── Slideshow helper ────────────────────────────────────────── */
const Slideshow = ({ images, className }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % images.length), 2600);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <>
      {images.map((src, i) => (
        <img key={src} src={src} alt="" className={`${className} ${i === idx ? 'is-visible' : ''}`} />
      ))}
    </>
  );
};

/* ─── Plain image (no frame) ──────────────────────────────────── */
const PlainMockup = ({ mockup }) => (
  <div className="dev-plain">
    <div className="dev-plain__glow" style={{ background: `radial-gradient(ellipse 80% 60% at 50% 90%, ${mockup.color}35 0%, transparent 70%)` }} />
    <Slideshow images={mockup.images} className="dev-plain__img" />
  </div>
);

/* ─── Browser frame ───────────────────────────────────────────── */
const BrowserMockup = ({ mockup }) => (
  <div className="dev-browser">
    <div className="dev-browser__bar">
      <div className="dev-browser__dots">
        <span style={{ background: '#ff5f57' }} /><span style={{ background: '#febc2e' }} /><span style={{ background: '#28c840' }} />
      </div>
      <div className="dev-browser__url">🔒 qlickhealth.ai</div>
    </div>
    <div className="dev-browser__screen">
      <Slideshow images={mockup.images} className="dev-browser__img" />
    </div>
  </div>
);

/* ─── Robot (plain photo) ─────────────────────────────────────── */
const RobotMockup = ({ mockup }) => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  return (
    <div className="dev-robot">
      <div className="dev-robot__glow" style={{ background: `radial-gradient(ellipse 70% 50% at 50% 85%, ${mockup.color}40 0%, transparent 70%)` }} />
      <img src={mockup.images[0]} alt="Autism Therapy Robot" className="dev-robot__img" />
      <div className="dev-robot__label" style={{ color: mockup.color }}>{isAr ? 'نظام علاج بالذكاء الاصطناعي' : 'AI Therapy System'}</div>
    </div>
  );
};

/* ─── Platform split (phone + browser) ───────────────────────── */
const PlatformMockup = ({ mockup }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % 2), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="dev-platform">
      <div className="dev-platform__phone">
        <div className="dev-platform__notch" />
        <div className="dev-platform__screen">
          {mockup.images.slice(0, 2).map((src, i) => (
            <img key={src} src={src} alt="" className={`dev-platform__img ${i === idx ? 'is-visible' : ''}`} />
          ))}
        </div>
        <div className="dev-platform__home" />
      </div>
      <div className="dev-platform__web">
        <div className="dev-platform__webbar">
          <span className="dev-platform__webdots"><b /><b /><b /></span>
          <span className="dev-platform__weburl">ondemand.wiz-tech.co</span>
        </div>
        <div className="dev-platform__webscreen">
          {mockup.images[2] && <img src={mockup.images[2]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />}
        </div>
      </div>
    </div>
  );
};

const Renderer = ({ mockup }) => {
  switch (mockup.type) {
    case 'plain':    return <PlainMockup mockup={mockup} />;
    case 'browser':  return <BrowserMockup mockup={mockup} />;
    case 'robot':    return <RobotMockup mockup={mockup} />;
    case 'platform': return <PlatformMockup mockup={mockup} />;
    default:         return null;
  }
};

/* ─── Peek thumbnail (small prev/next card) ───────────────────── */
const PeekCard = ({ mockup, onClick, side }) => (
  <button
    onClick={onClick}
    className="psc-peek"
    style={{ '--peek-color': mockup.color }}
    aria-label={`Go to ${mockup.title}`}
    title={mockup.title}
  >
    <div className="psc-peek__img-wrap">
      <img
        src={mockup.images[0]}
        alt={mockup.title}
        className="psc-peek__img"
      />
      <div className="psc-peek__overlay" />
    </div>
    <div className="psc-peek__label">{mockup.label}</div>
    <div className="psc-peek__arrow">
      {side === 'prev'
        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
      }
    </div>
  </button>
);

const MOCKUPS_AR = [
  { id: 'quran', type: 'plain', label: 'تطبيق جوال', tag: 'إسلامي · ذكاء اصطناعي', color: '#8258c8', title: 'قرآن AI — ترتيل', desc: 'حفظ القرآن بالذكاء الاصطناعي مع اكتشاف أخطاء التلاوة فورياً وتغذية راجعة لحظية.', images: ['/assets/images/projects/AI-Quran-App/1.png', '/assets/images/projects/AI-Quran-App/2.png', '/assets/images/projects/AI-Quran-App/3.png'] },
  { id: 'medical', type: 'browser', label: 'منصة ويب', tag: 'تقنية الصحة · SaaS', color: '#45d0bd', title: 'المنصة الطبية', desc: 'إدارة مستشفى بلا أخطاء — مواعيد وسجلات وبوابات أطباء.', images: ['/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.11.38.png', '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.29.48.png', '/assets/images/projects/Medical-Platform/Screenshot 2024-04-25 at 2.12.10.png'] },
  { id: 'kiosk', type: 'plain', label: 'نظام كشك', tag: 'تقنية المطاعم · POS', color: '#2c84c8', title: 'كشك الطلب الذاتي DineOS', desc: 'كشك طلب بشاشة لمس — يخفض وقت الانتظار 3 أضعاف ويوفر 30% من التكاليف.', images: ['/assets/images/kiosk-bg.png'] },
  { id: 'robot', type: 'robot', label: 'روبوتات', tag: 'ذكاء اصطناعي · علاج · صحة', color: '#45d0bd', title: 'روبوت علاج التوحد', desc: 'رفيق علاجي بالذكاء الاصطناعي يتكيف في الوقت الفعلي مع سلوك كل طفل.', images: ['/assets/images/robot/190610_NB1_Rasiing Hand_with Ear_Ted-1.png'] },
  { id: 'platform', type: 'platform', label: 'منصة OnDemand', tag: 'متعدد المنصات · SaaS', color: '#8258c8', title: 'منصة OnDemand', desc: 'SaaS متعدد المنصات — تطبيق جوال أصلي للعملاء الميدانيين ولوحة ويب قوية.', images: ['/assets/images/products/ondemand/Android Compact - 1.png', '/assets/images/products/ondemand/Android Compact - 2.png', '/assets/images/products/ondemand/MAIN.png'] },
];

/* ─── Main ────────────────────────────────────────────────────── */
const ProjectShowcase = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const mockups = isAr ? MOCKUPS_AR : MOCKUPS;
  const [active, setActive] = useState(0);
  const total = mockups.length;
  const m     = mockups[active];
  const prev  = mockups[(active - 1 + total) % total];
  const next  = mockups[(active + 1) % total];

  const goPrev = () => setActive(i => (i - 1 + total) % total);
  const goNext = () => setActive(i => (i + 1) % total);

  return (
    <section className="psc-section">
      <div className="container">

        {/* Header */}
        <div className="psc-header">
          <div className="psc-badge">
            <span className="psc-badge__dot" />
            <span>{isAr ? 'أعمالنا في الواقع' : 'Our Work in Action'}</span>
          </div>
          <h2 className="psc-title">
            {isAr ? <>مُسلَّم عبر <span className="psc-title__accent">كل منصة</span></> : <>Shipped Across <span className="psc-title__accent">Every Platform</span></>}
          </h2>
          <p className="psc-subtitle">
            {isAr ? 'من تطبيقات الجوال بالذكاء الاصطناعي إلى الأكشاك الذكية وروبوتات العلاج — WIZ تُسلِّم على كل جهاز.' : 'From AI mobile apps to smart kiosks and therapy robots — WIZ delivers on every form factor.'}
          </p>
        </div>

        {/* Tabs */}
        <div className="psc-tabs">
          {mockups.map((mk, i) => (
            <button
              key={mk.id}
              onClick={() => setActive(i)}
              className={`psc-tab ${active === i ? 'psc-tab--active' : ''}`}
              style={{
                borderColor: active === i ? mk.color : 'transparent',
                color: active === i ? '#fff' : 'rgba(255,255,255,0.5)',
                boxShadow: active === i ? `0 0 20px ${mk.color}40` : 'none',
              }}
            >
              {mk.label}
            </button>
          ))}
        </div>

        {/* Nav row: [peek-prev] [main] [peek-next] */}
        <div className="psc-nav-row">

          {/* Prev peek */}
          <PeekCard mockup={prev} onClick={goPrev} side="prev" />

          {/* Arrow button prev */}
          <button className="psc-arrow psc-arrow--prev" onClick={goPrev} aria-label="Previous">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          {/* Main display */}
          <div className="psc-display">
            <div className="psc-visual">
              <div className="psc-glow" style={{ background: `radial-gradient(ellipse 70% 60% at 50% 60%, ${m.color}22 0%, transparent 70%)` }} />
              <Renderer mockup={m} />
            </div>

            <div className="psc-info">
              <div className="psc-info__tag" style={{ color: m.color, borderColor: `${m.color}50`, background: `${m.color}12` }}>
                {m.tag}
              </div>
              <h3 className="psc-info__title">{m.title}</h3>
              <p className="psc-info__desc">{m.desc}</p>

              <div className="psc-info__type">
                <span className="psc-info__dot" style={{ background: m.color, boxShadow: `0 0 8px ${m.color}` }} />
                <span>{m.label}</span>
              </div>

              {/* Dot indicators */}
              <div className="psc-dots">
                {mockups.map((mk, i) => (
                  <button
                    key={mk.id}
                    onClick={() => setActive(i)}
                    className={`psc-dot ${active === i ? 'psc-dot--active' : ''}`}
                    style={{ background: active === i ? mk.color : 'rgba(255,255,255,0.15)', width: active === i ? 22 : 7 }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Arrow button next */}
          <button className="psc-arrow psc-arrow--next" onClick={goNext} aria-label="Next">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>

          {/* Next peek */}
          <PeekCard mockup={next} onClick={goNext} side="next" />
        </div>

      </div>
    </section>
  );
};

export default ProjectShowcase;
